/**
 * Translation API.
 *
 *   // messages/onboarding/index.ts
 *   export const onboardingMessages = defineMessages("onboarding", en);
 *
 *   // a component
 *   const t = useT(onboardingMessages);
 *   t("welcome", { name });            // "Willkommen, Juan"
 *   t("files", { count: 3 });          // plural forms by Intl.PluralRules
 *   t.rich("hint", { kbd: (k) => <kbd>{k}</kbd> });
 *
 *   // outside React (re-read on every call, so call it at render/speak time)
 *   translate(onboardingMessages, "welcome", { name });
 *
 * See `glossary.md` for the agreed German and Chinese terms.
 */
import { useSyncExternalStore, type ReactNode } from "react";

import { lowerFirst } from "./lower-first";
import { selectPluralForm } from "./plural";
import { formatRich, type RichValues } from "./rich";
import { activeLanguage, lookupMessage, subscribeLanguage } from "./runtime";
import type {
  AppLanguage,
  MessageArea,
  MessageArgs,
  MessageCatalogue,
  MessageValue,
  ParamValue,
} from "./types";

export type { AppLanguage, Messages, MessageArea } from "./types";
export { APP_LANGUAGES } from "./types";
export {
  activateLanguage,
  activeLanguage,
  activeLocaleTag,
  LOCALE_TAGS,
  subscribeLanguage,
} from "./runtime";
export {
  formatDate,
  formatList,
  formatNumber,
  formatRelativeTime,
  formatTime,
} from "./format";

/** Declares one area's catalogue. `area` is its folder under `messages/`. */
export function defineMessages<E extends MessageCatalogue>(
  area: string,
  en: E,
): MessageArea<E> {
  return { area, en };
}

type Params = { readonly [name: string]: ParamValue } | undefined;

/** `{name}` takes the param as is; `{name|lower}` lower-cases its first
 *  letter, for a label placed mid-sentence in languages that need it. */
function interpolate(text: string, params: Params): string {
  if (!params) return text;
  return text.replace(
    /\{([^}|]+)(?:\|(lower))?\}/g,
    (match, name: string, modifier?: string) => {
      if (!(name in params)) return match;
      const value = String(params[name]);
      return modifier === "lower" ? lowerFirst(value) : value;
    },
  );
}

function resolveText(
  value: MessageValue | undefined,
  key: string,
  params: Params,
): string {
  if (value === undefined) return key;
  if (typeof value === "string") return interpolate(value, params);
  const count = Number(params?.count ?? 0);
  return interpolate(selectPluralForm(value, count), params);
}

/** The message `key` of `messages` in the rendered language. */
export function translate<
  E extends MessageCatalogue,
  K extends keyof E & string,
>(messages: MessageArea<E>, key: K, ...args: MessageArgs<E[K]>): string {
  return resolveText(
    lookupMessage(messages.area, messages.en, key),
    key,
    args[0] as Params,
  );
}

export type Translator<E extends MessageCatalogue> = {
  <K extends keyof E & string>(key: K, ...args: MessageArgs<E[K]>): string;
  /** A message with `<tag>…</tag>` markup or React values in its params. */
  rich: <K extends keyof E & string>(key: K, values: RichValues) => ReactNode;
};

function translator<E extends MessageCatalogue>(
  messages: MessageArea<E>,
): Translator<E> {
  const t = (<K extends keyof E & string>(key: K, ...args: MessageArgs<E[K]>) =>
    translate(messages, key, ...args)) as Translator<E>;
  t.rich = (key, values) => {
    const value = lookupMessage(messages.area, messages.en, key);
    const count = typeof values.count === "number" ? values.count : 0;
    const text =
      value === undefined
        ? key
        : typeof value === "string"
          ? value
          : selectPluralForm(value, count);
    return formatRich(text, values);
  };
  return t;
}

// One translator per area and language: `t` stays the same object inside
// effect dependencies until the language actually changes.
const translators = new WeakMap<
  object,
  Partial<Record<AppLanguage, unknown>>
>();

function translatorFor<E extends MessageCatalogue>(
  messages: MessageArea<E>,
  language: AppLanguage,
): Translator<E> {
  let byLanguage = translators.get(messages);
  if (!byLanguage) {
    byLanguage = {};
    translators.set(messages, byLanguage);
  }
  byLanguage[language] ??= translator(messages);
  return byLanguage[language] as Translator<E>;
}

/** The translator for one area; re-renders the component on language change. */
export function useT<E extends MessageCatalogue>(
  messages: MessageArea<E>,
): Translator<E> {
  return translatorFor(messages, useAppLanguage());
}

/** The rendered app language, reactive. */
export function useAppLanguage() {
  return useSyncExternalStore(
    subscribeLanguage,
    activeLanguage,
    activeLanguage,
  );
}
